'use client';

import { useRef } from 'react';
import Image from 'next/image';
import Autoplay from 'embla-carousel-autoplay';
import { tv } from 'tailwind-variants';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Banner } from '@medprev-web-app/api-graphql-client';

import { BannerSheetComponent } from './banner-sheet.component';

import { useBannerSheet } from '@/app/(main)/(pages)/_hooks/use-banner-sheet.hook';
import { resolveBannerClickAction } from '@/app/(main)/(pages)/_utils/banner-click-action.util';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel.component';

const bannerSectionComponentStyle = tv({
  slots: {
    container: 'w-full md:flex md:flex-1 md:justify-center my-10 overflow-hidden',
    arrows: 'hidden',
  },
  variants: {
    hasImage: {
      true: { arrows: 'md:flex' },
    },
  },
});

const imageClass = tv({
  base: 'h-full w-full rounded-xl transition-opacity duration-200 ease-in-out',
  variants: {
    clickable: {
      true: 'cursor-pointer hover:opacity-90',
      false: 'cursor-default',
    },
  },
  defaultVariants: {
    clickable: false,
  },
});

interface BannersSectionProps {
  banners: Banner[];
}

export const BannersSection = ({ banners }: BannersSectionProps) => {
  const plugin = useRef(Autoplay({ delay: 4000, stopOnInteraction: true }));
  const { container, arrows } = bannerSectionComponentStyle();
  const { isBannerSheetOpen, setBannerSheetOpen, bannerSheetProps, handleBannerClick } = useBannerSheet();

  return (
    <>
      {banners?.length > 0 && (
        <section data-testid={DataTestIdEnum.CITY_BANNER_SECTION} className={container()}>
          <BannerSheetComponent
            open={isBannerSheetOpen}
            setOpen={setBannerSheetOpen}
            {...bannerSheetProps}
          />

          <div className="w-full md:mx-auto md:max-w-xl xl:max-w-5xl 3xl:max-w-7xl">
            <Carousel
              data-testid={DataTestIdEnum.CITY_BANNER_SECTION_CAROUSEL}
              plugins={[plugin.current]}
              onMouseEnter={plugin.current.stop}
              onMouseLeave={plugin.current.reset}
              orientation="horizontal"
              opts={{ loop: false }}
              showArrows
              arrowsClassName={arrows({ hasImage: banners.length > 0 })}
            >
              <CarouselContent className="ms-5 me-5 md:ms-0 md:me-0 gap-2">
                {banners.map((item) => (
                  <CarouselItem
                    key={item.id}
                    className="
                      pl-0 flex items-center justify-center
                      w-[21rem] h-[7rem]
                      md:w-full md:h-[11.75rem]
                      xl:w-[21rem] xl:h-[7rem]
                      3xl:w-[26.3rem] 3xl:h-[8rem]
                      rounded-xl"
                  >
                    <Image
                      className={imageClass({
                        clickable: resolveBannerClickAction(item) !== null,
                      })}
                      src={item.src || ''}
                      alt={item.alt || ''}
                      width={Number(item.width)}
                      height={Number(item.height)}
                      loading="lazy"
                      onClick={() => handleBannerClick(item)}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </section>
      )}
    </>
  );
};
